if (!customElements.get('media-horizontal-scroll')) {
  customElements.define('media-horizontal-scroll', class MediaHorizontalScroll extends HTMLElement {
    constructor() {
      super();
      this.curr = 0;
      this.target = 0;
      this.raf = null;
      this.isIntersecting = false;
      this.elementOffsetTop = 0;
      this.isMobileDevice = false;
      this.originalItems = [];
      this.isInitialized = false;
      this.isDragging = false;
      this.startX = 0;
      this.dragOffset = 0;
      this.startDragOffset = 0;
      this.resizer = null;
      this.lastWidth = 0;
      this.winH = window.innerHeight;
      this.lastWinWidth = window.innerWidth;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.updateValues = this.updateValues.bind(this);

      this.parentShopifySection = this.closest('.shopify-section');
      this.preloadNextSection();
    }

    connectedCallback() {
      requestAnimationFrame(() => this.init());
    }

    init() {
      this.scaleLayer = this.querySelector('.media-hor-scroll__scale');
      this.track = this.querySelector('.media-hor-scroll__track');

      if (!this.scaleLayer || !this.track) return;

      if (this.originalItems.length === 0) {
        this.originalItems = Array.from(this.track.children);
      }

      this.baseSpeed = parseFloat(this.getAttribute('data-speed')) / 50 || 0.5;
      this.ease = parseFloat(this.getAttribute('data-ease')) || 0.08;
      this.scaleAmount = parseFloat(this.getAttribute('data-scale')) / 100 || 0.5;
      this.isReverse = this.hasAttribute('data-reverse');

      this.scaleLayer.style.willChange = 'transform';
      this.track.style.willChange = 'transform';
      this.scaleLayer.style.transformOrigin = this.isReverse ? 'right bottom' : 'left bottom';

      this.isMobileDevice = DeviceUtils.isMobileSmall;

      this.waitForLayout().then(() => {
        if (!this.isInitialized) {
          this.initDragEvents();
          this.initObserver();

          window.addEventListener('resize', () => {
            if (window.innerWidth !== this.lastWinWidth) {
              this.lastWinWidth = window.innerWidth;
              this.updateValues();
            }
          }, { passive: true });

          this.resizer = new ResizeObserver((entries) => {
            for (let entry of entries) {
              const newWidth = Math.round(entry.contentRect.width);
              if (this.lastWidth !== newWidth) {
                this.lastWidth = newWidth;
                this.isMobileDevice = window.DeviceUtils ? DeviceUtils.isMobileSmall : window.innerWidth < 577;
                this.refresh();
              }
            }
          });
          this.resizer.observe(this);
          this.isInitialized = true;
        }
      });
    }
    preloadNextSection() {
      if (!this.parentShopifySection) return;

      const nextSection = this.parentShopifySection.nextElementSibling;
      if (!nextSection || !nextSection.classList.contains('shopify-section') || nextSection.dataset.preloaded) return;

      nextSection.dataset.preloaded = 'true';

      const lazySection = nextSection.querySelector('lazy-section-template');
      if (lazySection && !lazySection.hydrated && typeof lazySection.hydrate === 'function') {
        lazySection.hydrate('forced_preload_from_media_scroll');
        return;
      }

      nextSection.querySelectorAll('img').forEach(img => {
        if (img.loading === 'lazy') img.loading = 'eager';
      });
    }

    updateValues() {
      this.winH = window.innerHeight;
      this.calc();
    }

    refresh() {
      this.dragOffset = 0;
      this.clearClones();

      requestAnimationFrame(() => {
        this.calc();
        this.setupClones();
        this.setInitialPosition();
      });
    }

    clearClones() {
      this.track.innerHTML = '';
      this.originalItems.forEach(item => {
        item.style.width = '';
        this.track.appendChild(item);
      });
    }

    onPointerDown(e) {
      this.isDragging = true;
      this.startX = e.clientX;
      this.startDragOffset = this.dragOffset;
      this.track.setPointerCapture(e.pointerId);
    }

    onPointerMove(e) {
      if (!this.isDragging) return;

      const x = e.clientX;
      const dist = (x - this.startX) * 1.5;

      let nextOffset = this.isReverse ? (this.startDragOffset + dist) : (this.startDragOffset - dist);

      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const relativeScroll = (scrollY + this.winH) - this.elementOffsetTop;
      const scrollBase = relativeScroll * this.speed;

      const minAllowedOffset = -scrollBase;
      const maxAllowedOffset = this.maxScroll - scrollBase;

      this.dragOffset = Math.max(minAllowedOffset, Math.min(maxAllowedOffset, nextOffset));
    }

    onPointerUp(e) {
      this.isDragging = false;
      if (e && e.pointerId) {
        this.track.releasePointerCapture(e.pointerId);
      }
    }

    onDragStart(e) {
      e.preventDefault();
    }

    initDragEvents() {
      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('dragstart', this.onDragStart);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
    }

    waitForLayout() {
      return new Promise((resolve) => {
        const check = () => {
          if (this.originalItems.length > 0 && this.originalItems[0].offsetWidth > 0) {
            resolve();
          } else {
            requestAnimationFrame(check);
          }
        };
        check();
      });
    }

    setupClones() {
      if (this.originalItems.length === 0) return;

      if (this.isMobileDevice) {
        const viewportWidth = window.innerWidth;
        this.maxScroll = Math.max(0, this.track.scrollWidth - viewportWidth);
        return;
      }

      const viewportWidth = window.innerWidth;
      const trackStyles = window.getComputedStyle(this.track);
      const gap = parseFloat(trackStyles.gap) || 0;

      let setWidth = 0;
      const originalWidths = this.originalItems.map(item => {
        const w = item.offsetWidth;
        setWidth += w + gap;
        return w;
      });

      if (setWidth <= 0) return;

      const multiplier = 2;
      const targetWidth = viewportWidth * multiplier;
      const copiesNeeded = Math.max(1, Math.ceil((targetWidth - setWidth) / setWidth));

      for (let i = 0; i < copiesNeeded; i++) {
        this.originalItems.forEach((item, index) => {
          const clone = item.cloneNode(true);
          clone.style.width = originalWidths[index] + 'px';
          this.track.appendChild(clone);
        });
      }
      this.maxScroll = Math.max(0, this.track.scrollWidth - viewportWidth);
    }

    calc() {
      const rect = this.getBoundingClientRect();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      this.elementOffsetTop = rect.top + scrollY;
      this.elementHeight = rect.height;
      this.speed = this.isMobileDevice ? this.baseSpeed * 2.5 : this.baseSpeed;
      if (this.isMobileDevice) {
        this.scaleLayer.style.transform = 'scale(1)';
      }
    }

    setInitialPosition() {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const relativeScroll = (scrollY + this.winH) - this.elementOffsetTop;

      this.target = Math.max(0, Math.min(this.maxScroll, (relativeScroll * this.speed) + this.dragOffset));
      this.curr = this.target;

      const xPos = this.isReverse ? (this.curr - this.maxScroll) : -this.curr;
      this.track.style.transform = `translate3d(${xPos.toFixed(4)}px, 0, 0)`;
    }

    initObserver() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isIntersecting = entry.isIntersecting;
          this.classList.toggle('is-visible', this.isIntersecting);
          if (this.isIntersecting) {
            this.calc();
            const scrollY = window.pageYOffset || document.documentElement.scrollTop;
            const relativeScroll = (scrollY + this.winH) - this.elementOffsetTop;
            this.target = Math.max(0, Math.min(this.maxScroll, (relativeScroll * this.speed) + this.dragOffset));
            this.curr = this.target;

            if (!this.raf) this.animate();
          } else {
            if (this.raf) {
              cancelAnimationFrame(this.raf);
              this.raf = null;
            }
          }
        });
      }, { rootMargin: '10% 0px' });
      this.observer.observe(this);
    }

    animate() {
      if (!this.isIntersecting) return;

      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const relativeScroll = (scrollY + this.winH) - this.elementOffsetTop;

      this.target = Math.max(0, Math.min(this.maxScroll, (relativeScroll * this.speed) + this.dragOffset));

      const diff = this.target - this.curr;

      if (Math.abs(diff) > 0.1) {
        const currentEase = this.isMobileDevice ? 0.12 : this.ease;
        this.curr += diff * currentEase;
      } else {
        this.curr = this.target;
      }

      const xPos = this.isReverse ? (this.curr - this.maxScroll) : -this.curr;
      this.track.style.transform = `translate3d(${xPos.toFixed(2)}px, 0, 0)`;

      if (!this.isMobileDevice) {
        const totalDistance = this.winH + this.elementHeight;
        const progress = Math.max(0, Math.min(1, relativeScroll / totalDistance));
        const scaleProgress = progress > 0.4 ? (progress - 0.4) / 0.6 : 0;
        const scale = 1 - (scaleProgress * this.scaleAmount);
        this.scaleLayer.style.transform = `scale(${scale.toFixed(4)})`;
      }

      this.raf = requestAnimationFrame(() => this.animate());
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.observer) this.observer.disconnect();
      if (this.resizer) this.resizer.disconnect();

      window.removeEventListener('resize', this.updateValues);

      this.track.removeEventListener('pointerdown', this.onPointerDown);
      this.track.removeEventListener('dragstart', this.onDragStart);
      this.track.removeEventListener('pointermove', this.onPointerMove);
      this.track.removeEventListener('pointerup', this.onPointerUp);
      this.track.removeEventListener('pointercancel', this.onPointerUp);
    }
  });
}